/**
 * Thin fetch wrapper. Cookies carry the session, so every call sends
 * credentials and nothing stores a token in JavaScript.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    let code = 'request_failed';
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) code = body.error;
    } catch {
      // Non-JSON error body; keep the generic code.
    }
    throw new ApiError(response.status, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const withBody = (method: string) => <T>(path: string, body?: unknown) =>
  request<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: withBody('POST'),
  put: withBody('PUT'),
  patch: withBody('PATCH'),
  /**
   * Used by exactly one screen, for the one route in this application that
   * destroys anything (DECISIONS.md 2026-08-22). If a second caller appears,
   * that is worth a conversation rather than a code review.
   */
  del: withBody('DELETE'),
};

export interface ClinicianSessionInfo {
  clinician: { displayName: string };
  context: 'DASHBOARD' | 'IN_OFFICE_RUNNER';
}

// =============================================================================
// Clinician core (Phase 3)
//
// Note what is absent from PatientSummary: there is no name, no contact detail,
// nothing identifying (constraint 1). A patient is a code. The doctor knows who
// that is from his separate offline app, and this platform never does.
// =============================================================================

export interface PatientSummary {
  id: string;
  code: string;
  createdAt: string;
  deactivatedAt: string | null;
  accessExpiresOn: string | null;
  publishedVersion: number | null;
  publishedAt: string | null;
  hasDraft: boolean;
}

/** The exercise vocabulary, matching the registry contract exactly. */
export type PositionValue = 'seated' | 'standing' | 'tandem';
export type InputModeValue = 'keyboard' | 'mouse' | 'touch';

export interface PrescribedExercise {
  /** Absent until the row has been saved once. */
  id?: string;
  exerciseId: string;
  params: Record<string, number | string | boolean>;
  position: PositionValue;
  inputMode: InputModeValue;
  patientNote?: string | null;
}

export interface ProgramSessionDraft {
  label: string;
  exercises: PrescribedExercise[];
}

/**
 * How a program runs (§6). Two numbers, not one.
 *
 * `aimPerDay` is what the doctor prescribes — the patient is told it, and
 * adherence is measured against it. `maxPerDay` is a safety ceiling and is
 * NEVER sent to a patient screen: the moment one can say "2 of 5" it is a
 * progress bar, which in this population is an instruction to reach 5
 * (constraint 3). See DECISIONS.md 2026-08-22.
 */
export interface ScheduleDraft {
  /** Ordered session labels, repeated as needed: ["A","A","B"]. */
  rotation: string[];
  aimPerDay: number;
  maxPerDay: number;
  minGapHours: number;
}

export interface PrescriptionView {
  id: string;
  version: number;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  publishedAt: string | null;
  publishNote: string | null;
  sessions: ProgramSessionDraft[];
  schedule: ScheduleDraft;
}

/**
 * The per-patient safety settings (§7, Phase 5).
 *
 * NOT part of the prescription and not published with it — they are properties
 * of the patient that outlive any one program. Sent alongside it only because
 * the form that edits them lives on the same screen.
 *
 * The scale each one is on is not the same, and the server module
 * `safety/settings.ts` is where that is explained: the delta is in steps of the
 * 3-point scale the runner samples at every checkpoint, the ceiling is on the
 * 0-10 detail scale that exists only when the patient gives a number.
 */
export interface SafetySettings {
  symptomDeltaTrigger: number;
  symptomAbsoluteCeiling: number;
  maxSessionMinutes: number;
  maxExerciseMinutes: number;
  decayMonitorEnabled: boolean;
  decaySensitivity: number;
  startSubThreshold: boolean;
}

export interface PrescriptionResponse {
  patient: {
    id: string;
    code: string;
    deactivatedAt: string | null;
    safety: SafetySettings;
  };
  draft: PrescriptionView | null;
  published: PrescriptionView | null;
}

// =============================================================================
// Patient side (Phase 4)
//
// Note again what is absent: the patient's own screens know a code, a set of
// exercises, and a screen calibration. There is nothing here that could
// identify anybody, including to the person sitting in front of it.
// =============================================================================

export interface RunExercise {
  instanceId: string;
  orderIndex: number;
  exerciseId: string;
  exerciseVersion: number;
  params: Record<string, number | string | boolean>;
  position: PositionValue;
  inputMode: InputModeValue;
  /** The doctor's short patient-facing note, shown before the exercise runs. */
  patientNote: string | null;
}

export interface TherapyCalibration {
  pxPerDegree: number;
  viewingDistanceIn: number;
  screenWidthPx: number;
  screenHeightPx: number;
}

export type TherapyToday =
  /**
   * `beyondAim` says this session is optional extra work — nothing more. It is
   * a boolean rather than a count on purpose: there is no number in it with
   * which to build a progress bar.
   */
  | {
      state: 'ready';
      label: string;
      exercises: RunExercise[];
      beyondAim: boolean;
      remainingToAim: number;
    }
  /** Within the gap since the last session. */
  | { state: 'waiting'; readyAt: string; remainingToAim: number }
  /** The day's ceiling is reached. The ceiling itself is never sent. */
  | { state: 'done_for_today' }
  /**
   * §9 Layer 5 rule 2 — a safety event today ended the day. A separate state
   * from `done_for_today` because the patient needs different words: one has
   * finished their therapy, the other stopped partway and has done nothing
   * wrong.
   */
  | { state: 'locked_out' }
  | { state: 'no_program' };

export interface TherapyState {
  code: string;
  lowStimulusMode: boolean;
  educationSeen: boolean;
  calibration: TherapyCalibration | null;
  daysSinceLastSession: number | null;
  askDelayedOnset: boolean;
  today: TherapyToday;
}

/** 1 green, 2 yellow, 3 red (§8). Never presented as a score. */
export type SymptomRating = 1 | 2 | 3;

export type SymptomDetail = Partial<Record<string, number>>;

/** Why today's session is not running. Both values are about today only. */
export type BlockedReason = 'pre_session_red' | 'within_day_rise';

export interface StartRunResponse {
  runId: string;
  blocked: boolean;
  blockedReason: BlockedReason | null;
  label: string;
  /** Already at the dose that will actually run — see `reducedDose`. */
  exercises: RunExercise[];
  /**
   * True when today is running short (§9 Layer 5 rule 3, or §7's first week).
   * The patient is told this; they are never told which rule caused it.
   */
  reducedDose: boolean;
  beyondAim: boolean;
  /**
   * The thresholds the runner applies itself, because the transition tap has to
   * decide synchronously to keep its full screen gesture. Nothing renders them.
   * See `pages/therapy/decide.ts`.
   */
  safety: RunnerSafety;
}

/**
 * The thresholds sent to the patient's browser at the start of a session.
 *
 * A wire shape, so it lives here with the others. The rule that reads it is
 * `pages/therapy/decide.ts`. Deliberately a SUBSET of the doctor's safety
 * settings: `startSubThreshold` never comes here because the server has already
 * applied it, and nothing about the day's ceiling comes here at all.
 */
export interface RunnerSafety {
  symptomDeltaTrigger: number;
  symptomAbsoluteCeiling: number;
  maxSessionMinutes: number;
  maxExerciseMinutes: number;
  decayMonitorEnabled: boolean;
  decaySensitivity: number;
}

export interface ExerciseResultPayload {
  exerciseInstanceId: string | null;
  exerciseId: string;
  exerciseVersion: number;
  orderIndex: number;
  position: PositionValue;
  inputMode: InputModeValue;
  paramsUsed: Record<string, number | string | boolean>;
  /** What the screen could actually deliver, when that differed. */
  deliveredParams: Record<string, number> | null;
  symptomRating: SymptomRating | null;
  startedAt: string;
  endedAt: string;
  completed: boolean;
  stopReason: string | null;
  samples: Array<{
    t: number;
    metric: string;
    value: number;
    detail?: Record<string, number | string | boolean>;
  }>;
}
